import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Check,
  ChevronDown,
  ExternalLink,
  Eye,
  Link2,
  Loader2,
  Mail,
  MailOpen,
  Pencil,
  Save,
  Shield,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Switch,
  Tabs,
  Textarea,
} from '../../components/ui';
import {
  BattleNetIcon,
  EpicGamesIcon,
  RiotIcon,
  SteamIcon,
  XboxIcon,
} from '../../components/icons/BrandIcons';
import { getPlatformProfileUrl } from '../../components/icons/PlatformIcon';
import { trpc } from '../../lib/trpc';

type SettingsTab = 'profile' | 'accounts' | 'invites' | 'privacy' | 'blocked';

export const Route = createFileRoute('/app/settings')({
  staticData: { title: 'Settings' },
  validateSearch: (search: Record<string, unknown>): { tab: SettingsTab } => {
    const tab = search.tab as string;
    if (tab === 'profile' || tab === 'accounts' || tab === 'invites' || tab === 'privacy' || tab === 'blocked') {
      return { tab };
    }
    return { tab: 'profile' };
  },
  component: SettingsPage,
});

type PlatformId = 'riot' | 'steam' | 'microsoft' | 'epic' | 'battlenet';

type PlatformConfig = {
  id: PlatformId;
  name: string;
  icon: (props: { className?: string }) => React.ReactNode;
  color: string;
  usernameLabel: string;
  usernamePlaceholder: string;
  hasDiscriminator: boolean;
  discriminatorPlaceholder?: string;
  idLabel: string;
  idPlaceholder: string;
};

const platforms: PlatformConfig[] = [
  {
    id: 'steam',
    name: 'Steam',
    icon: SteamIcon,
    color: 'text-surface-200',
    usernameLabel: 'Steam Username',
    usernamePlaceholder: 'Your Steam display name',
    hasDiscriminator: false,
    idLabel: 'Steam ID (optional)',
    idPlaceholder: 'e.g. 76561198...',
  },
  {
    id: 'epic',
    name: 'Epic Games',
    icon: EpicGamesIcon,
    color: 'text-surface-200',
    usernameLabel: 'Epic Display Name',
    usernamePlaceholder: 'Your Epic username',
    hasDiscriminator: false,
    idLabel: 'Epic Account ID (optional)',
    idPlaceholder: 'Leave blank if you don\'t know it',
  },
  {
    id: 'microsoft',
    name: 'Xbox / Microsoft',
    icon: XboxIcon,
    color: 'text-green-400',
    usernameLabel: 'Gamertag',
    usernamePlaceholder: 'Your Xbox Gamertag',
    hasDiscriminator: false,
    idLabel: 'XUID (optional)',
    idPlaceholder: 'Leave blank if you don\'t know it',
  },
  {
    id: 'riot',
    name: 'Riot Games',
    icon: RiotIcon,
    color: 'text-red-400',
    usernameLabel: 'Riot ID',
    usernamePlaceholder: 'Player',
    hasDiscriminator: true,
    discriminatorPlaceholder: 'NA1',
    idLabel: 'PUUID (optional)',
    idPlaceholder: 'Leave blank if you don\'t know it',
  },
  {
    id: 'battlenet',
    name: 'Battle.net',
    icon: BattleNetIcon,
    color: 'text-sky-400',
    usernameLabel: 'BattleTag',
    usernamePlaceholder: 'Player',
    hasDiscriminator: true,
    discriminatorPlaceholder: '1234',
    idLabel: 'Battle.net ID (optional)',
    idPlaceholder: 'Leave blank if you don\'t know it',
  },
];

const settingsTabs = [
  { id: 'profile', label: 'Profile' },
  { id: 'accounts', label: 'Game Accounts' },
  { id: 'invites', label: 'Invites', icon: <Mail className="size-4" /> },
  { id: 'privacy', label: 'Privacy' },
  { id: 'blocked', label: 'Blocked Users', icon: <Shield className="size-4" /> },
];

function SettingsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const setActiveTab = (tabId: string) => {
    navigate({
      to: '/app/settings',
      search: { tab: tabId as SettingsTab },
      replace: true,
    });
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>
      <Tabs tabs={settingsTabs} defaultTab={tab} onChange={setActiveTab} />

      {tab === 'profile' && <ProfileSettings />}
      {tab === 'accounts' && <GameAccountSettings />}
      {tab === 'invites' && <PendingInvitesSettings />}
      {tab === 'privacy' && <PrivacySettings />}
      {tab === 'blocked' && <BlockedUsersSettings />}
    </div>
  );
}

function ProfileSettings() {
  const { data: user } = trpc.user.me.useQuery();
  const utils = trpc.useUtils();
  const [bio, setBio] = useState(user?.bio ?? '');

  const updateProfile = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      utils.user.me.invalidate();
    },
  });

  const handleSave = () => {
    updateProfile.mutate({ bio: bio || undefined });
  };

  return (
    <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50 space-y-6">
      <h2 className="text-lg font-semibold">Profile</h2>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-surface-200 mb-1.5 block">Display Name</label>
          <p className="text-sm text-surface-300">
            Your display name is managed through Clerk. Use the account button in the navbar to change it.
          </p>
        </div>

        <Textarea
          label="Bio"
          placeholder="Tell others about yourself..."
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
        />
        <p className="text-xs text-surface-400">{bio.length}/500 characters</p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={updateProfile.isPending}
          leftIcon={<Save className="size-4" />}
        >
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

function GameAccountSettings() {
  const { data: accounts, isLoading } = trpc.linkedAccount.list.useQuery();
  const utils = trpc.useUtils();
  const [expandedPlatform, setExpandedPlatform] = useState<PlatformId | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{ id: string; name: string } | null>(null);

  const linkedPlatforms = new Set(accounts?.map((a) => a.platform) ?? []);

  const unlinkMutation = trpc.linkedAccount.unlink.useMutation({
    onSuccess: () => {
      toast.success('Account unlinked');
      utils.linkedAccount.list.invalidate();
      setUnlinkTarget(null);
    },
    onError: () => toast.error('Failed to unlink account'),
  });

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-display font-bold flex items-center gap-2 text-white">
            <Link2 className="size-5" />
            Game Accounts
          </h2>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[72px] rounded-xl bg-surface-700/30 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {platforms.map((platform) => {
              const Icon = platform.icon;
              const isLinked = linkedPlatforms.has(platform.id);
              const linkedAccount = accounts?.find((a) => a.platform === platform.id);
              const isExpanded = expandedPlatform === platform.id && !isLinked;
              const isEditing = editingAccountId === linkedAccount?.id;
              const profileUrl = linkedAccount
                ? getPlatformProfileUrl(platform.id, linkedAccount.platformUserId, linkedAccount.platformUsername)
                : null;

              return (
                <div
                  key={platform.id}
                  className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                    isLinked
                      ? 'border-brand-500/30 bg-brand-500/5'
                      : isExpanded
                        ? 'border-surface-600 bg-surface-800/60'
                        : 'border-surface-700/40 bg-surface-800/30 hover:border-surface-600'
                  }`}
                >
                  {/* Header row */}
                  <div className="flex items-center gap-4 px-5 py-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (!isLinked) {
                          setExpandedPlatform(isExpanded ? null : platform.id);
                        }
                      }}
                      className="flex items-center gap-4 flex-1 min-w-0 text-left"
                    >
                      <div
                        className={`flex items-center justify-center size-10 rounded-xl transition-colors shrink-0 ${
                          isLinked
                            ? 'bg-brand-500/15 text-brand-400'
                            : `bg-surface-700/50 ${platform.color}`
                        }`}
                      >
                        <Icon className="size-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm ${isLinked ? 'text-white' : 'text-surface-200'}`}>
                          {platform.name}
                        </p>
                        {isLinked && linkedAccount && (
                          <p className="text-xs text-brand-400/80 mt-0.5 truncate">
                            {linkedAccount.platformUsername}
                            {linkedAccount.platformUsernameDiscriminator && (
                              <span className="text-brand-400/50">#{linkedAccount.platformUsernameDiscriminator}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </button>

                    {isLinked && linkedAccount ? (
                      <div className="flex items-center gap-2 shrink-0">
                        {linkedAccount.verified ? (
                          <Badge color="green" variant="subtle" size="sm">
                            <Shield className="size-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="subtle" size="sm">Manual</Badge>
                        )}
                        {profileUrl && (
                          <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="!px-2">
                              <ExternalLink className="size-3.5 text-surface-400" />
                            </Button>
                          </a>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="!px-2"
                          onClick={() => setEditingAccountId(isEditing ? null : linkedAccount.id)}
                        >
                          <Pencil className="size-3.5 text-surface-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="!px-2"
                          onClick={() => setUnlinkTarget({ id: linkedAccount.id, name: platform.name })}
                        >
                          <Trash2 className="size-3.5 text-red-400" />
                        </Button>
                      </div>
                    ) : (
                      <ChevronDown
                        className={`size-4 text-surface-500 transition-transform duration-200 shrink-0 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </div>

                  {/* Edit username form (for linked accounts) */}
                  {isLinked && isEditing && linkedAccount && (
                    <div className="px-5 pb-5 pt-1 border-t border-surface-700/30">
                      <EditUsernameForm
                        accountId={linkedAccount.id}
                        platform={platform}
                        currentUsername={linkedAccount.platformUsername}
                        currentDiscriminator={linkedAccount.platformUsernameDiscriminator ?? ''}
                        onDone={() => setEditingAccountId(null)}
                      />
                    </div>
                  )}

                  {/* Expanded: link form (for unlinked accounts) */}
                  {isExpanded && (
                    <ExpandedLinkSection
                      platform={platform}
                      onSuccess={() => {
                        setExpandedPlatform(null);
                        utils.linkedAccount.list.invalidate();
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unlink confirmation modal */}
      {unlinkTarget && (
        <Modal open={true} onClose={() => setUnlinkTarget(null)} className="!max-w-sm">
          <div className="px-6 pt-6 pb-1 flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20">
              <Trash2 className="size-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-display font-bold text-white">Unlink Account</h2>
              <p className="text-sm text-surface-400">This cannot be undone</p>
            </div>
          </div>
          <ModalBody>
            <p className="text-sm text-surface-300">
              Are you sure you want to unlink your <span className="font-semibold text-white">{unlinkTarget.name}</span> account?
              You'll need to re-link it if you want to use it again.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setUnlinkTarget(null)} disabled={unlinkMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="!bg-red-500 hover:!bg-red-600"
              disabled={unlinkMutation.isPending}
              leftIcon={unlinkMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              onClick={() => unlinkMutation.mutate({ accountId: unlinkTarget.id })}
            >
              {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

function ExpandedLinkSection({
  platform,
  onSuccess,
}: {
  platform: PlatformConfig;
  onSuccess: () => void;
}) {
  const [showManualForm, setShowManualForm] = useState(false);

  const handleOAuthClick = () => {
    toast('OAuth linking coming soon!', {
      description: `${platform.name} sign-in will be available in a future update.`,
      icon: <Zap className="size-4 text-brand-400" />,
    });
  };

  return (
    <div className="px-5 pb-5 pt-1 border-t border-surface-700/30 animate-fade-in">
      {!showManualForm ? (
        <div className="space-y-4 pt-3">
          <Button
            variant="primary"
            className="w-full"
            leftIcon={<ExternalLink className="size-4" />}
            onClick={handleOAuthClick}
          >
            Link with {platform.name}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-surface-700/50" />
            <span className="text-[11px] uppercase tracking-wider text-surface-600 font-medium">or</span>
            <div className="flex-1 h-px bg-surface-700/50" />
          </div>
          <Button
            variant="ghost"
            className="w-full"
            size="sm"
            onClick={() => setShowManualForm(true)}
          >
            Enter details manually
          </Button>
        </div>
      ) : (
        <div className="pt-3">
          <button
            type="button"
            onClick={() => setShowManualForm(false)}
            className="text-xs font-medium text-surface-500 hover:text-surface-300 transition-colors mb-3"
          >
            &larr; Back
          </button>
          <ManualLinkForm platform={platform} onSuccess={onSuccess} />
        </div>
      )}
    </div>
  );
}

function ManualLinkForm({
  platform,
  onSuccess,
}: {
  platform: PlatformConfig;
  onSuccess: () => void;
}) {
  const [username, setUsername] = useState('');
  const [discriminator, setDiscriminator] = useState('');
  const [userId, setUserId] = useState('');

  const linkMutation = trpc.linkedAccount.linkManual.useMutation({
    onSuccess: () => {
      toast.success(`${platform.name} account linked!`);
      onSuccess();
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        toast.error(`You already have a ${platform.name} account linked`);
      } else {
        toast.error(`Failed to link ${platform.name} account`);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    linkMutation.mutate({
      platform: platform.id,
      platformUserId: userId.trim() || username.trim(),
      platformUsername: username.trim(),
      platformUsernameDiscriminator: discriminator.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {platform.hasDiscriminator ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={platform.usernameLabel}
              placeholder={platform.usernamePlaceholder}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className="w-28">
            <Input
              label="Tag"
              placeholder={platform.discriminatorPlaceholder}
              value={discriminator}
              onChange={(e) => setDiscriminator(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <Input
          label={platform.usernameLabel}
          placeholder={platform.usernamePlaceholder}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      )}
      <Input
        label={platform.idLabel}
        placeholder={platform.idPlaceholder}
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />

      {linkMutation.isError && (
        <p className="text-sm text-red-400">{linkMutation.error.message}</p>
      )}

      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!username.trim() || linkMutation.isPending}
          leftIcon={linkMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : undefined}
        >
          {linkMutation.isPending ? 'Linking...' : 'Link Account'}
        </Button>
      </div>
    </form>
  );
}

function EditUsernameForm({
  accountId,
  platform,
  currentUsername,
  currentDiscriminator,
  onDone,
}: {
  accountId: string;
  platform: PlatformConfig;
  currentUsername: string;
  currentDiscriminator: string;
  onDone: () => void;
}) {
  const utils = trpc.useUtils();
  const [username, setUsername] = useState(currentUsername);
  const [discriminator, setDiscriminator] = useState(currentDiscriminator);

  const updateMutation = trpc.linkedAccount.updateUsername.useMutation({
    onSuccess: () => {
      toast.success('Username updated');
      utils.linkedAccount.list.invalidate();
      onDone();
    },
    onError: () => toast.error('Failed to update username'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    updateMutation.mutate({
      accountId,
      platformUsername: username.trim(),
      platformUsernameDiscriminator: discriminator.trim() || null,
    });
  };

  const hasChanges = username.trim() !== currentUsername || discriminator.trim() !== currentDiscriminator;

  return (
    <form onSubmit={handleSubmit} className="pt-3 space-y-3">
      <p className="text-xs text-surface-500 font-medium uppercase tracking-wider">Edit Username</p>
      {platform.hasDiscriminator ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={platform.usernameLabel}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className="w-28">
            <Input
              label="Tag"
              placeholder={platform.discriminatorPlaceholder}
              value={discriminator}
              onChange={(e) => setDiscriminator(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <Input
          label={platform.usernameLabel}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!username.trim() || !hasChanges || updateMutation.isPending}
          leftIcon={updateMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Pending Invites Settings Tab (#50)
// ---------------------------------------------------------------------------

function PendingInvitesSettings() {
  const utils = trpc.useUtils();
  const { data: invites, isLoading } = trpc.clubInvite.listPending.useQuery();

  const acceptMutation = trpc.clubInvite.accept.useMutation({
    onSuccess: () => {
      toast.success('Invite accepted!');
      utils.clubInvite.listPending.invalidate();
      utils.club.getMyClubs.invalidate();
    },
    onError: (err) => toast.error(err.message || 'Failed to accept invite'),
  });

  const declineMutation = trpc.clubInvite.decline.useMutation({
    onSuccess: () => {
      toast.success('Invite declined');
      utils.clubInvite.listPending.invalidate();
    },
    onError: () => toast.error('Failed to decline invite'),
  });

  return (
    <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
      <h2 className="text-lg font-display font-bold flex items-center gap-2 text-white mb-6">
        <Mail className="size-5" />
        Pending Invites
      </h2>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-surface-700/30 animate-pulse" />
          ))}
        </div>
      ) : invites && invites.length > 0 ? (
        <div className="space-y-3">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center gap-4 p-4 rounded-xl bg-surface-900/50 border border-surface-700/50"
            >
              <div className="size-12 rounded-xl bg-surface-700/50 flex items-center justify-center shrink-0 overflow-hidden">
                {invite.clubAvatarUrl ? (
                  <img src={invite.clubAvatarUrl} alt="" className="size-full object-cover" />
                ) : (
                  <span className="text-lg font-display font-bold text-surface-400">
                    {invite.clubName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white truncate">{invite.clubName}</p>
                <p className="text-xs text-surface-400 mt-0.5">
                  Invited by <span className="text-surface-300">{invite.inviterUsername}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="!text-red-400 hover:!bg-red-500/10"
                  disabled={declineMutation.isPending}
                  onClick={() => declineMutation.mutate({ inviteId: invite.id })}
                >
                  <X className="size-4" />
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={acceptMutation.isPending}
                  leftIcon={acceptMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
                  onClick={() => acceptMutation.mutate({ inviteId: invite.id })}
                >
                  Accept
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MailOpen className="size-10 text-surface-500 mb-3" />
          <p className="text-sm font-medium text-surface-300">No pending invites</p>
          <p className="text-xs text-surface-500 mt-1">
            When someone invites you to a club, it will appear here
          </p>
        </div>
      )}
    </div>
  );
}

function BlockedUsersSettings() {
  const utils = trpc.useUtils();
  const { data: blockedUsers, isLoading } = trpc.block.list.useQuery();

  const unblockMutation = trpc.block.remove.useMutation({
    onSuccess: () => {
      toast.success('User unblocked');
      utils.block.list.invalidate();
    },
    onError: () => toast.error('Failed to unblock user'),
  });

  return (
    <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50">
      <h2 className="text-lg font-display font-bold flex items-center gap-2 text-white mb-6">
        <Shield className="size-5" />
        Blocked Users
      </h2>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-surface-700/30 animate-pulse" />
          ))}
        </div>
      ) : blockedUsers && blockedUsers.length > 0 ? (
        <div className="space-y-3">
          {blockedUsers.map((block) => (
            <div
              key={block.id}
              className="flex items-center gap-4 p-4 rounded-xl bg-surface-900/50 border border-surface-700/50"
            >
              <div className="size-10 rounded-full bg-surface-700/50 flex items-center justify-center shrink-0 overflow-hidden">
                {block.avatarUrl ? (
                  <img src={block.avatarUrl} alt="" className="size-full object-cover rounded-full" />
                ) : (
                  <span className="text-sm font-display font-bold text-surface-400">
                    {block.username.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white truncate">{block.username}</p>
                <p className="text-xs text-surface-500 mt-0.5">
                  Blocked {new Date(block.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="!text-red-400 hover:!bg-red-500/10"
                disabled={unblockMutation.isPending}
                onClick={() => unblockMutation.mutate({ blockedUserId: block.blockedUserId })}
              >
                Unblock
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Shield className="size-10 text-surface-500 mb-3" />
          <p className="text-sm font-medium text-surface-300">No blocked users</p>
          <p className="text-xs text-surface-500 mt-1">
            When you block someone, they'll appear here
          </p>
        </div>
      )}
    </div>
  );
}

function PrivacySettings() {
  const { data: user } = trpc.user.me.useQuery();
  const utils = trpc.useUtils();

  const updatePrivacy = trpc.user.updatePrivacy.useMutation({
    onSuccess: () => {
      utils.user.me.invalidate();
    },
  });

  if (!user) return null;

  return (
    <div className="p-6 rounded-2xl bg-surface-800/50 border border-surface-700/50 space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Eye className="size-5" />
        Privacy Settings
      </h2>

      <div className="space-y-4">
        <Switch
          label="Public Profile"
          description="When enabled, anyone can see your full profile. When disabled, only basic info is shown."
          checked={user.isPublic}
          onChange={(e) => {
            updatePrivacy.mutate({ isPublic: e.target.checked });
          }}
        />
      </div>
    </div>
  );
}
